import { useRef, useCallback } from 'react';
import {
  useRangeCalendar,
  useCalendarGrid,
  useCalendarCell,
  useButton,
  useLocale,
  useFocusRing,
  usePress,
  mergeProps,
} from 'react-aria';
import { useRangeCalendarState } from 'react-stately';
import {
  createCalendar,
  getWeeksInMonth,
  type DateValue,
  CalendarDate,
  today,
  getLocalTimeZone,
} from '@internationalized/date';
import { cn } from '@antimatterhq/ui/lib/utils';
import { Button } from '@antimatterhq/ui/components/button';
import { Text } from '@antimatterhq/ui/components/typography';

export interface MultiDateRangePickerProps {
  className?: string;
  value?: { start: CalendarDate; end: CalendarDate } | null;
  onChange?: (range: { start: DateValue; end: DateValue } | null) => void;
  minValue?: CalendarDate;
}

const MultiDateRangePickerCalendarCell = ({
  state,
  date,
  currentMonth,
}: {
  state: ReturnType<typeof useRangeCalendarState>;
  date: CalendarDate;
  currentMonth: CalendarDate;
}) => {
  const ref = useRef<HTMLButtonElement>(null);

  const { cellProps, buttonProps, isSelected, isDisabled, formattedDate } =
    useCalendarCell({ date }, state, ref);

  const { focusProps, isFocusVisible } = useFocusRing();
  const { pressProps, isPressed } = usePress(buttonProps);

  const isSelectionStart =
    state.highlightedRange && state.highlightedRange.start.compare(date) === 0;
  const isSelectionEnd =
    state.highlightedRange && state.highlightedRange.end.compare(date) === 0;
  const isOutsideMonth =
    date.month !== currentMonth.month || date.year !== currentMonth.year;

  // Check if this is today's date
  const todayDate = today(getLocalTimeZone());
  const isToday = date.compare(todayDate) === 0;

  return (
    <td {...cellProps} className="h-9 w-9 p-0">
      <button
        {...mergeProps(buttonProps, pressProps, focusProps)}
        ref={ref}
        className={cn(
          'hover relative flex h-9 w-9 cursor-pointer items-center justify-center text-sm',
          isOutsideMonth && 'invisible',
          !isOutsideMonth && !isDisabled && 'text-gray-900 dark:text-gray-100',
          isDisabled && 'cursor-not-allowed opacity-50',
          !isSelected &&
            !isDisabled &&
            !isOutsideMonth &&
            'hover:rounded hover:bg-gray-100 dark:hover:bg-gray-800',
          isSelected &&
            !isSelectionStart &&
            !isSelectionEnd &&
            !isOutsideMonth &&
            'bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700',
          (isSelectionStart || isSelectionEnd) &&
            !isOutsideMonth &&
            'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-700',
          isSelectionStart && !isOutsideMonth && 'rounded-l',
          isSelectionEnd && !isOutsideMonth && 'rounded-r',
          isFocusVisible &&
            !isOutsideMonth &&
            'outline outline-2 outline-offset-2 outline-blue-500',
          isPressed && 'scale-95',
        )}
      >
        <span className="relative">
          {formattedDate}
          {isToday && !isOutsideMonth && (
            <span className="absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-blue-600 dark:bg-blue-400" />
          )}
        </span>
      </button>
    </td>
  );
};

const MultiDateRangePickerCalendarGrid = ({
  state,
  startDate,
}: {
  state: ReturnType<typeof useRangeCalendarState>;
  startDate: CalendarDate;
}) => {
  const { locale } = useLocale();
  const { gridProps, headerProps, weekDays } = useCalendarGrid(
    { startDate, endDate: startDate },
    state,
  );

  const weeksInMonth = getWeeksInMonth(startDate, locale);

  return (
    <table {...gridProps} className="w-full border-collapse border-spacing-0">
      <thead {...headerProps}>
        <tr>
          {weekDays.map((day, index) => (
            <th
              key={index}
              className="p-1 text-xs font-medium text-gray-500 dark:text-gray-400"
            >
              {day}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[...new Array(weeksInMonth).keys()].map((weekIndex) => (
          <tr key={weekIndex}>
            {state
              .getDatesInWeek(weekIndex, startDate)
              .map((date, i) =>
                date ? (
                  <MultiDateRangePickerCalendarCell
                    key={i}
                    state={state}
                    date={date}
                    currentMonth={startDate}
                  />
                ) : (
                  <td key={i} className="h-9 w-9 p-0" />
                ),
              )}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const MultiDateRangePicker = ({
  className,
  value,
  onChange,
  minValue,
}: MultiDateRangePickerProps) => {
  const { locale } = useLocale();

  const state = useRangeCalendarState({
    value,
    onChange,
    locale,
    createCalendar,
    visibleDuration: { months: 2 },
    pageBehavior: 'single',
    minValue,
  });

  const ref = useRef<HTMLDivElement>(null);
  const { calendarProps, prevButtonProps, nextButtonProps } = useRangeCalendar(
    {
      value,
      onChange,
      minValue,
    },
    state,
    ref,
  );

  const prevButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);

  const { buttonProps: prevProps } = useButton(prevButtonProps, prevButtonRef);
  const { buttonProps: nextProps } = useButton(nextButtonProps, nextButtonRef);

  const firstMonth = state.visibleRange.start;
  const secondMonth = firstMonth.add({ months: 1 });

  const handleTodayClick = useCallback(() => {
    const todayDate = today(getLocalTimeZone());
    state.setFocusedDate(todayDate);
  }, [state]);

  return (
    <div {...calendarProps} ref={ref} className={cn('p-4', className)}>
      <header className="relative mb-4 flex w-full items-center justify-between">
        <Button
          {...prevProps}
          ref={prevButtonRef}
          size="small"
          iconOnly
          leftIcon="lucide:chevron-left"
          className="absolute left-0 h-9"
        />
        <div className="grid flex-1 grid-cols-2 items-center justify-center gap-8">
          <Text className="mx-auto" semiBold>
            {firstMonth
              .toDate(Intl.DateTimeFormat().resolvedOptions().timeZone)
              .toLocaleDateString(locale, { month: 'long', year: 'numeric' })}
          </Text>
          <Text className="mx-auto" semiBold>
            {secondMonth
              .toDate(Intl.DateTimeFormat().resolvedOptions().timeZone)
              .toLocaleDateString(locale, { month: 'long', year: 'numeric' })}
          </Text>
        </div>
        <Button
          {...nextProps}
          ref={nextButtonRef}
          size="small"
          iconOnly
          leftIcon="lucide:chevron-right"
          className="absolute right-0 h-9"
        />
      </header>
      <div className="mb-4 flex justify-center">
        <Button
          onClick={handleTodayClick}
          size="small"
          variant="ghost"
          className="text-xs"
        >
          Today
        </Button>
      </div>
      <div className="flex gap-8">
        <MultiDateRangePickerCalendarGrid
          state={state}
          startDate={firstMonth}
        />
        <MultiDateRangePickerCalendarGrid
          state={state}
          startDate={secondMonth}
        />
      </div>
    </div>
  );
};
